/**
 * AddPlatformDialog — wizard to add a new platform under existing default author.
 *
 * Two modes:
 *   1. URL onboard       → POST /author/onboard with mode='add-platform'
 *                          (BD scrape + AI mapping). Backend preserves authorId,
 *                          does NOT touch author-level fields.
 *   2. Manual            → frontend writes platform doc directly via
 *                          platformService.addPlatformManual (copies
 *                          system_platforms catalog defaults). User can paste
 *                          posts manually later or run Re-analyze with empty
 *                          corpus.
 *
 * Step 1: select platform from system catalog (filters out already-enabled)
 * Step 2: choose mode + provide URL OR confirm manual + (URL mode) available model selector
 *         + maxPosts inputs via ConfirmActionDialog inline.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import {
  getAuthorPlatformService,
  type SystemPlatformLite,
  type AuthorBasePath,
} from '@modules/content-plan/services/AuthorPlatformService';
import { ContentPlanApiClient } from '@modules/content-plan/services/ContentPlanApiClient';
import { useProjectDefaultModel } from '@modules/content-plan/hooks/useProjectDefaultModel';

const TESTID = 'add-platform-dialog';
const DEFAULT_MAX_POSTS = 10;

export interface AddPlatformDialogProps {
  open: boolean;
  projectId: string;
  authorId: string;
  userId: string;
  /** locale defaults from author.defaultLocale */
  defaultLocale?: string;
  /** Discriminates self-author vs tracked creator. Default `'authors'`. */
  basePath?: AuthorBasePath;
  onClose: () => void;
  /** Notified when add-platform task starts so a parent task tray can subscribe. */
  onTaskStarted?: (taskId: string, label: string) => void;
}

type Mode = 'url' | 'manual';

export const AddPlatformDialog: React.FC<AddPlatformDialogProps> = ({
  open,
  projectId,
  authorId,
  userId,
  defaultLocale = 'en-US',
  basePath = 'authors',
  onClose,
  onTaskStarted,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const platformService = getAuthorPlatformService(basePath);
  const [systemPlatforms, setSystemPlatforms] = useState<SystemPlatformLite[]>([]);
  const [enabledIds, setEnabledIds] = useState<string[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogLoadError, setCatalogLoadError] = useState<string | null>(null);

  const [selectedPlatformId, setSelectedPlatformId] = useState<string>('');
  const [mode, setMode] = useState<Mode>('url');
  const [profileUrl, setProfileUrl] = useState('');
  const [model, setModel] = useState<string>(projectDefaultModel.value);
  const [maxPostsNum, setMaxPostsNum] = useState<number>(DEFAULT_MAX_POSTS);
  const [allPosts, setAllPosts] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on reopen + load catalog
  useEffect(() => {
    if (!open) return;
    setSelectedPlatformId('');
    setMode('url');
    setProfileUrl('');
    setModel(projectDefaultModel.value);
    setMaxPostsNum(DEFAULT_MAX_POSTS);
    setAllPosts(false);
    setSubmitting(false);
    setError(null);
    setCatalogLoaded(false);
    setCatalogLoadError(null);

    let cancelled = false;
    setLoadingCatalog(true);
    Promise.all([
      platformService.getSystemPlatforms(),
      platformService.listEnabledPlatformIds({ projectId, authorId }),
    ])
      .then(([sys, enabled]) => {
        if (cancelled) return;
        setSystemPlatforms(sys);
        setEnabledIds(enabled);
        setCatalogLoaded(true);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setSystemPlatforms([]);
          setEnabledIds([]);
          setCatalogLoadError(e?.message ?? 'Failed to load system platforms');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, authorId, projectDefaultModel.value, platformService]);

  // Available platforms = system catalog minus already-enabled
  const availablePlatforms = useMemo(
    () => systemPlatforms.filter((p) => !enabledIds.includes(p.platformId)),
    [systemPlatforms, enabledIds],
  );

  const canSubmit = useMemo(() => {
    if (!selectedPlatformId) return false;
    if (mode === 'url' && !profileUrl.trim()) return false;
    return true;
  }, [selectedPlatformId, mode, profileUrl]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'url') {
        // URL mode: dispatch backend handler with mode='add-platform'.
        // Backend: scrape posts via BD + AI mapping → write voice/examples,
        // skipping author-level identity overwrite.
        const client = new ContentPlanApiClient();
        const res = await client.authorOnboard({
          projectId,
          userId,
          // schema 1.3.1+ extends mode enum with 'add-platform'
          // schema 1.4.0+: authorKind selects external_authors collection.
          mode: 'add-platform' as any,
          profileUrl: profileUrl.trim(),
          targetAudienceIds: [],
          locale: defaultLocale,
          existingAuthorId: authorId,
          // schema-level options
          ...({
            model,
            maxPosts: allPosts ? ('all' as any) : maxPostsNum,
            // pass platformId hint via manualInput-like field (handler will
            // detect target platform from URL via PlatformDetector if absent)
            manualInput: { platformId: selectedPlatformId, postTexts: [] },
            ...(basePath === 'external_authors'
              ? { authorKind: 'tracked-creator' }
              : {}),
          } as any),
        });
        if (!res.success || !res.taskId) {
          throw new Error(res.error ?? 'add-platform task dispatch failed');
        }
        onTaskStarted?.(res.taskId, `Add ${selectedPlatformId} platform`);
      } else {
        // Manual mode: write platform doc directly, no backend round-trip.
        await platformService.addPlatformManual({
          projectId,
          authorId,
          platformId: selectedPlatformId,
        });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to add platform');
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TESTID}
    >
      <DialogHeader title="Add platform" onClose={submitting ? undefined : onClose} />
      <DialogContent>
        <Stack spacing={2}>
          {/* Step 1 — platform selection */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Choose platform
            </Typography>
            {loadingCatalog ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading catalog…</Typography>
              </Box>
            ) : catalogLoadError ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                {catalogLoadError}
              </Alert>
            ) : !catalogLoaded ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading catalog…</Typography>
              </Box>
            ) : catalogLoaded && systemPlatforms.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                System platform catalog is empty. Seed system_platforms before adding platforms.
              </Alert>
            ) : catalogLoaded && availablePlatforms.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                All platforms from system catalog are already enabled for this author.
              </Alert>
            ) : (
              <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={selectedPlatformId}
                  label="Platform"
                  onChange={(e) => setSelectedPlatformId(e.target.value)}
                  data-testid={`${TESTID}-platformSelect`}
                >
                  {availablePlatforms.map((p) => (
                    <MenuItem key={p.platformId} value={p.platformId}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{p.displayName}</Typography>
                        {p.maxPostLength != null && (
                          <Chip
                            size="small"
                            label={`max ${p.maxPostLength}c`}
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Step 2 — mode */}
          {selectedPlatformId && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  How to populate this platform
                </Typography>
                <RadioGroup
                  value={mode}
                  onChange={(e) => setMode(e.target.value as Mode)}
                >
                  <FormControlLabel
                    value="url"
                    control={<Radio size="small" data-testid={`${TESTID}-modeUrl`} />}
                    label={
                      <Box>
                        <Typography variant="body2">URL onboard (scrape with AI analysis)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Fetches posts via BrightData, runs AI to draft voice + examples.
                          Author-level identity (positioning, etc.) is NOT overwritten.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="manual"
                    control={<Radio size="small" data-testid={`${TESTID}-modeManual`} />}
                    label={
                      <Box>
                        <Typography variant="body2">Manual (no scrape, no AI)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Just create the platform with system defaults. You'll paste
                          posts later and run Re-analyze when ready.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>

              {mode === 'url' && (
                <>
                  <TextField
                    label={`${selectedPlatformId} profile URL`}
                    size="small"
                    fullWidth
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    placeholder={`https://www.${selectedPlatformId}.com/...`}
                    inputProps={{ 'data-testid': `${TESTID}-profileUrl` }}
                  />
                  <AvailableModelSelector
                    projectId={projectId}
                    value={model}
                    onChange={setModel}
                    providers={['google']}
                    label="AI model"
                    size="small"
                  />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      label="Max posts to scrape"
                      size="small"
                      type="number"
                      disabled={allPosts}
                      value={allPosts ? '' : maxPostsNum}
                      onChange={(e) => setMaxPostsNum(Number(e.target.value) || 0)}
                      inputProps={{ min: 1, max: 1000, 'data-testid': `${TESTID}-maxPosts` }}
                      sx={{ width: 200 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allPosts}
                          onChange={(e) => setAllPosts(e.target.checked)}
                          data-testid={`${TESTID}-allPosts`}
                        />
                      }
                      label="All posts"
                    />
                  </Stack>
                </>
              )}
            </>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        alignment="right"
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          disabled: submitting,
          'data-testid': `${TESTID}-cancel`,
        }}
        primaryAction={{
          label: submitting ? 'Adding…' : 'Add platform',
          onClick: handleSubmit,
          disabled: !canSubmit || submitting,
          loading: submitting,
          'data-testid': `${TESTID}-submit`,
        }}
      />
    </BaseDialog>
  );
};

export default AddPlatformDialog;
